import {
  DocumentSubType,
  documentSubTypeLabels,
  aircraftDocumentSubTypes,
  complianceDocumentSubTypes,
  crewDocumentSubTypes,
  passengerDocumentSubTypes,
} from '@@api/shared'
import {
  generateAircraftS3Key,
  generateComplianceS3Key,
  generateS3Key,
} from '@/lib/hooks/s3'
import type { DocumentFormOwner } from './types'

interface OwnerConfig {
  editTitle: string
  // Stored Document.category this owner writes (trip docs are Compliance docs)
  category: 'Crew' | 'Passenger' | 'Aircraft' | 'Compliance'
  subTypeOptions: { value: DocumentSubType; label: string }[]
  defaultSubType?: DocumentSubType
  // accept attr for the hidden file input; undefined = any file type
  fileAccept?: string
  fields: {
    documentNumber: boolean
    issuer: boolean
    issueDate: boolean
    isPublic: boolean
    version: boolean
    // minDate bound + expired/expiring hint text under the expiry row
    expiryHints: boolean
    // "you won't be notified" AlertDialog when saving without an expiry date
    confirmMissingExpiry: boolean
  }
  generateS3Key: (ownerId: string, subType: DocumentSubType, fileName: string) => string
}

const toOptions = (subTypes: readonly DocumentSubType[]) =>
  subTypes.map((type) => ({ value: type, label: documentSubTypeLabels[type] }))

// Compliance/trip keys are per-subType, not per-owner-record
const complianceKey = (_ownerId: string, subType: DocumentSubType, fileName: string) =>
  generateComplianceS3Key(subType, fileName)

export const ownerConfigs: Record<DocumentFormOwner['type'], OwnerConfig> = {
  crew: {
    editTitle: 'Edit Crew Document',
    category: 'Crew',
    fileAccept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    subTypeOptions: toOptions(crewDocumentSubTypes),
    fields: {
      documentNumber: true,
      issuer: true,
      issueDate: true,
      isPublic: true,
      version: false,
      expiryHints: true,
      confirmMissingExpiry: true,
    },
    generateS3Key,
  },
  passenger: {
    editTitle: 'Edit Passenger Document',
    category: 'Passenger',
    fileAccept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    subTypeOptions: toOptions(passengerDocumentSubTypes),
    fields: {
      documentNumber: true,
      issuer: true,
      issueDate: true,
      isPublic: true,
      version: false,
      expiryHints: true,
      confirmMissingExpiry: true,
    },
    // Passenger docs historically live under crew/{passengerId}/ — keys are
    // opaque, so keep the prefix rather than orphan a path convention.
    generateS3Key,
  },
  aircraft: {
    editTitle: 'Edit Aircraft Document',
    category: 'Aircraft',
    fileAccept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    subTypeOptions: toOptions(aircraftDocumentSubTypes),
    fields: {
      documentNumber: true,
      issuer: true,
      issueDate: true,
      isPublic: true,
      version: false,
      expiryHints: true,
      confirmMissingExpiry: false,
    },
    generateS3Key: generateAircraftS3Key,
  },
  compliance: {
    editTitle: 'Edit Document',
    category: 'Compliance',
    subTypeOptions: toOptions(complianceDocumentSubTypes),
    defaultSubType: DocumentSubType.Manual,
    fields: {
      documentNumber: false,
      issuer: false,
      issueDate: false,
      isPublic: false,
      version: true,
      expiryHints: false,
      confirmMissingExpiry: false,
    },
    generateS3Key: complianceKey,
  },
  trip: {
    editTitle: 'Edit Document', // trip owner is create-only; never shown
    category: 'Compliance',
    subTypeOptions: toOptions(complianceDocumentSubTypes),
    defaultSubType: DocumentSubType.Manual,
    fields: {
      documentNumber: true,
      issuer: true,
      issueDate: true,
      isPublic: true,
      version: false, // stored version is hardcoded to '1' on create
      expiryHints: false,
      confirmMissingExpiry: false,
    },
    generateS3Key: complianceKey,
  },
}
